// src/pages/About.js
import React, { useEffect, useState } from "react";
import "../styles/About.css";
import Footer from "../components/Footer";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

import pinkSun from "../assets/about/pink-sun.png";
import arrow from "../assets/about/arrow.png";
import bulb from "../assets/about/bulb.png";
import highlight from "../assets/about/highlight.png";
import orangeStar from "../assets/about/orange-star.png";
import photoFrame from "../assets/about/photo-frame.png";
import sushe from "../assets/about/sushe.png";
import buttonDecor from "../assets/about/button-decor.png";

const About = () => {
  const [heroBg, setHeroBg] = useState("");
  const [dynamicPhotoUrl, setDynamicPhotoUrl] = useState("");

  // All dynamic text with safe fallbacks to your current copy
  const [copy, setCopy] = useState({
    heroTitle: "About Me",
    heroSubtitle: "Here's a glimpse into my journey and what inspires my work.",
    headingTop: "I design",
    headingTopEmphasis: "✍️ top",
    headingBottom: "notch",
    highlightText: "websites",
    ctaLabel: "See Portfolio",
    ctaHref: "/portfolio",
    aboutText: "",          // optional HTML alternative
    aboutParagraphs: null,  // optional array of strings
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const ref = doc(db, "pageContent", "about");
        const snap = await getDoc(ref);
        if (!snap.exists()) return;

        const d = snap.data();

        const strOr = (v, fb) =>
          typeof v === "string" && v.trim().length ? v : fb;

        setHeroBg(strOr(d.heroBackground, ""));
        setDynamicPhotoUrl(strOr(d.aboutImage, ""));

        // Normalize paragraphs: ensure array of non-empty trimmed strings
        const normalizedParas =
          Array.isArray(d.aboutParagraphs)
            ? d.aboutParagraphs
                .map((p) => (typeof p === "string" ? p.trim() : ""))
                .filter((p) => p.length)
            : null;

        setCopy((prev) => ({
          ...prev,
          heroTitle: strOr(d.heroTitle ?? d.title, prev.heroTitle),
          heroSubtitle: strOr(d.heroSubtitle ?? d.subtitle, prev.heroSubtitle),
          headingTop: strOr(d.headingTop, prev.headingTop),
          headingTopEmphasis: strOr(d.headingTopEmphasis, prev.headingTopEmphasis),
          headingBottom: strOr(d.headingBottom, prev.headingBottom),
          highlightText: strOr(d.highlightText, prev.highlightText),
          ctaLabel: strOr(d.ctaLabel, prev.ctaLabel),
          ctaHref: strOr(d.ctaHref, prev.ctaHref),
          aboutText:
            typeof d.aboutText === "string" && d.aboutText.trim().length
              ? d.aboutText
              : prev.aboutText,
          aboutParagraphs: normalizedParas ?? prev.aboutParagraphs,
        }));
      } catch (err) {
        console.error("Error fetching About page data:", err);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="about-container">
      {/* Hero Section */}
      <div
        className="about-hero"
        style={{ backgroundImage: heroBg ? `url(${heroBg})` : undefined }}
      >
        <div className="about-hero-overlay"></div>
        <div className="about-hero-text">
          <h1>{copy.heroTitle}</h1>
          <p>{copy.heroSubtitle}</p>
        </div>
      </div>

      {/* Content Section */}
      <div className="about-content">
        <div className="intro-section">
          {/* Heading section (fully dynamic, same structure & styles) */}
          <div className="about-heading">
            <img src={pinkSun} alt="pink sun" className="pink-sun" />

            <h2>
              {copy.headingTop}{" "}
              <span className="highlight-word">{copy.headingTopEmphasis}</span>
              <br />
              {copy.headingBottom}{" "}
              <span className="highlight-image">
                <span className="highlight-text">{copy.highlightText}</span>
                <img src={highlight} alt="highlight" className="highlight-img" />
              </span>
            </h2>

            <a href={copy.ctaHref} className="portfolio-button">
              {copy.ctaLabel}
            </a>
            <img src={buttonDecor} alt="button decor" className="button-decor" />
          </div>

          {/* Photo Frame — unchanged crop system */}
          <div className="photo-frame-container">
            <img src={photoFrame} alt="photo frame" className="photo-frame" />

            {dynamicPhotoUrl && (
              <div className="photo-clip-wrapper">
                <img
                  src={dynamicPhotoUrl}
                  alt="Sukaksha Shetty"
                  className="photo-inside"
                />
              </div>
            )}

            {/* Decorative elements (positions unchanged) */}
            <img src={arrow} alt="arrow" className="arrow-graphic" />
            <img src={bulb} alt="bulb" className="bulb-icon" />
            <img src={orangeStar} alt="star" className="star-icon" />
            <img src={sushe} alt="sushe" className="sushe-tag" />
          </div>
        </div>

        {/* Description — paragraphs array > HTML > hardcoded fallback */}
        {Array.isArray(copy.aboutParagraphs) && copy.aboutParagraphs.length > 0 ? (
          <div className="about-description">
            {copy.aboutParagraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        ) : copy.aboutText ? (
          <div
            className="about-description"
            dangerouslySetInnerHTML={{ __html: copy.aboutText }}
          />
        ) : (
          <div className="about-description">
            <p>
              Hi, I’m Sukaksha Shetty. But most people know me as SuShe.
              <br />
              Based in Mysore, Karnataka, I blend digital creativity with handmade warmth.
              By day, I’m a freelancer offering design and content services.
              By heart, I’m someone who finds joy in creating gifts with my own hands.
            </p>
            <p>
              My love for crafting began in childhood and became my comfort space during tough days.
              Handmade gifts aren’t just a hobby. They’re how I connect with people.
              One of my favorites? A “Best Couple Trophy” I made for my cousin and his wife
              using recycled LED bulbs and a few decorative pieces. Seeing how much joy it brought
              them reminded me why I love what I do.
            </p>
            <p>
              Freelancing, on the other hand, was a dream on pause. After I left a job that
              didn’t value my work, that leap opened doors to something better: freedom,
              creativity, and the joy of working on my own terms. One of the projects I
              especially cherish is designing a Discord server for Rust Yatra, a project by the
              well-known YouTube streamer RakaZone Gaming. It wasn’t my first paid project,
              but definitely one of the earliest ones that gave me confidence in this new path.
            </p>
            <p>
              I’ve taught myself tools like Canva from scratch. I started with YouTube
              thumbnails and evolved into post creation and brand content. I also learned
              Figma through a friend who believed in my potential and shared his course access
              with me. That encouragement helped me fall in love with UI/UX design.
              I may not know it all, but I know how to design experiences that feel right.
            </p>
            <p>
              Recently, at my college alumni meet, a lecturer who watches my handmade gift
              tutorials on YouTube praised my creativity in front of others.
              That moment gave me the confidence boost I didn’t know I needed.
            </p>
            <p>
              What matters to me the most?
              Doing what I commit to. Submitting work on time. Welcoming feedback.
              And always staying true to the creative process.
              <br />
              This is just the start of my journey. I’m excited to create, collaborate, and grow
              one thoughtful project at a time.
            </p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default About;
